export interface DirectorDocumentTypeItem {
  id: string
  name: string
  is_active: boolean
  sort_order: number
}

export interface DirectorRejectionReasonItem {
  id: string
  reason: string
  is_active: boolean
  sort_order: number
}

export interface DirectorConfig {
  director_enabled: boolean
  director_gate_enabled: boolean
  // Direktorkonfig v2 (D8): Trigger-Arten + Doc-Regeln liegen in der
  // absence_types-Tabelle — diese Felder sind entfallen.
  director_duration_threshold_days: number
  director_no_substitute_escalation: boolean
  director_conditional_approval_required: boolean
  director_lesson_account_enabled: boolean
  director_finance_decider_requester: 'hr' | 'requester'
  director_finance_decider_substitute: 'hr' | 'substitute'
  director_routing_basis: 'teacher' | 'lesson'
  director_document_types: DirectorDocumentTypeItem[] | string[]
  director_rejection_reasons: DirectorRejectionReasonItem[] | string[]
  director_document_retention_days: number
  director_delegations_enabled: boolean
  director_max_concurrent_delegations: number
  active_directors_count?: number
  // Effektive Rechte des Direktors (Kap. 8); undefined = Backend älter
  can_approve?: boolean
  can_reject?: boolean
  can_info_request?: boolean
  can_request_documents?: boolean
  can_decide_payer?: boolean
  can_manual_assign?: boolean
  can_adjust_case?: boolean
  can_delegate?: boolean
  visibility_scope?: 'faculty' | 'school'
}

export interface DirectorDecision {
  id: string
  school_id: string
  substitution_request_id: string
  director_id: string
  director_name: string
  payment_type: 'private' | 'school'
  decision_type: 'approved' | 'rejected' | 'returned' | 'info_requested'
  rejection_reason: string | null
  rejection_note: string | null
  document_requests: string[]
  custom_document_request: string | null
  time_impact: 'deducted' | 'neutral' | 'credited'
  removed_lesson_ids?: string[]
  info_request_note?: string | null
  request_status?: string | null
  created_at: string
  updated_at: string
}

/** HR-Sicht: Backend liefert decision_id + requesting_teacher_name (Fix V2). */
export interface HRDirectorDecisionRow extends DirectorDecision {
  decision_id: string
  requesting_teacher_name: string
  time_impact_color?: string
  lesson_count?: number
}

export interface DirectorPendingItem {
  id: string
  requesting_teacher_name: string
  date: string
  subject_name: string | null
  lesson_count: number
  status: string
  reason: string | null
  created_at: string
  has_decision: boolean
  gate_triggers?: string[]
  absence_type?: string | null
  /** Direktorkonfig v2 (D10): Label-Snapshot — Anzeige vorrangig vor Key-Map */
  absence_type_label?: string | null
  conditional_approval?: boolean
  account_hold?: boolean
  // modal-v3/FG3 (E8): Dokumente-Status für die Dashboard-Spalte «Dokumente offen»
  documents_requested_at?: string | null
  documents_received?: boolean
  documents_deferred?: boolean
  document_due?: boolean
  has_document?: boolean
  // Fallakte v2 (P1/E2): Zeitraum, Klasse und Frist für die Fallliste
  date_to?: string | null
  klass_name?: string | null
  expires_at?: string | null
  kind?: 'request' | 'retro' | 'notice'
  gate_skipped_by_admin?: boolean
  created_on_behalf?: boolean
  payer_set?: boolean
  notice_badge?: string | null
}

export interface DirectorHistoryItem {
  decision_id: string
  decision_type: string
  payment_type: string
  time_impact: string
  director_name: string
  requesting_teacher_name: string
  date: string
  subject_name: string | null
  rejection_reason: string | null
  created_at: string
  /** Fallakte v2 (P1/E2): Verlauf in die Fallakte verlinkbar */
  substitution_request_id?: string
}

export interface DirectorDecisionCreatePayload {
  substitution_request_id: string
  payment_type: 'private' | 'school'
  decision_type: 'approved' | 'rejected' | 'returned' | 'info_requested'
  rejection_reason?: string
  rejection_note?: string
  document_requests?: string[]
  custom_document_request?: string
  removed_lesson_ids?: string[]
  info_request_note?: string
}

export interface DirectorRequestDetail {
  id: string
  requesting_teacher_id?: string | null
  requesting_teacher_last_name?: string | null
  requesting_teacher_name: string
  date: string | null
  date_to: string | null
  subject_name: string | null
  klass_name: string | null
  status: string
  reason: string | null
  absence_type: string | null
  /** Direktorkonfig v2 (D10): Label-Snapshot — Anzeige vorrangig vor Key-Map */
  absence_type_label?: string | null
  lesson_count: number
  gate_reason: Record<string, unknown> | null
  conditional_approval: boolean
  account_hold: boolean
  expires_at: string | null
  created_at: string | null
  lessons: Array<{
    id: string
    date: string | null
    time_slot_id: string | null
    time_slot_name?: string | null
    start_time?: string | null
    end_time?: string | null
    effective_start_time?: string | null
    effective_end_time?: string | null
    subject_name?: string | null
    klass_name?: string | null
    room_name?: string | null
    status: string | null
    assigned_teacher_id: string | null
    assigned_teacher_name?: string | null
    assigned_teacher_last_name?: string | null
    assigned_by: string | null
  }>
  latest_decision: {
    id: string
    decision_type: string
    payment_type: string
    created_at: string | null
    info_request_note: string | null
  } | null
  // E8/EC3: Dokumente-Status
  documents?: DirectorDocumentItem[]
  documents_received?: boolean
  documents_deferred?: boolean
  documents_requested_at?: string | null
  document_due?: boolean
  has_document?: boolean
  gate_skipped_by_admin?: boolean
  created_on_behalf?: boolean
}

/** Fallakte v2 (P4): ein Dokument einer Anfrage (Vorschau/Download). */
export interface DirectorDocumentItem {
  id: string
  filename: string
  file_size: number
  mime_type: string
  created_at: string | null
}

export const ABSENCE_TYPE_LABELS: Record<string, string> = {
  illness: 'Krankheit',
  training: 'Fortbildung',
  business_trip: 'Dienstreise',
  vacation: 'Urlaub',
  personal: 'Privat',
  other: 'Andere',
}

export const DECISION_TYPE_LABELS: Record<string, string> = {
  approved: 'Genehmigt',
  rejected: 'Abgelehnt',
  returned: 'Angepasst',
  info_requested: 'Rückfrage',
}

export const DECISION_TYPE_COLORS: Record<string, string> = {
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  returned: 'bg-orange-100 text-orange-800',
  info_requested: 'bg-blue-100 text-blue-800',
}

export const GATE_TRIGGER_LABELS: Record<string, string> = {
  absence_type: 'Abwesenheits-Art',
  duration: 'Entscheid ab Abwesenheitsdauer',
  lesson_account: 'Lektionenkonto',
  no_substitute: 'Kein Ersatz',
  partial_coverage_deadline: 'Teilabdeckung bis Frist',
}

export const TIME_IMPACT_LABELS: Record<string, string> = {
  deducted: 'Abgezogen',
  neutral: 'Nicht abgezogen',
  credited: 'Gutgeschrieben',
}

export const PAYMENT_TYPE_LABELS: Record<string, string> = {
  private: 'Privat',
  school: 'Schule',
}

export type WorkflowColumn = 'documents_pending' | 'ready'

/**
 * Fallakte v2 (P4): Minimal-Interface für Aktions-Modals — wird sowohl von
 * DirectorWorkflowItem (Dashboard) als auch vom Fallakte-Detail erfüllt.
 */
export interface DirectorActionItem {
  id: string
  requesting_teacher_name: string
  date: string | null
  lesson_count: number
  subject_name?: string | null
  reason?: string | null
  kind?: 'request' | 'retro' | 'notice'
}

/** Fallakte v2 (P3/E4): Audit-Event der Fall-Timeline. */
export interface DirectorTimelineEvent {
  type:
    | 'request_submitted'
    | 'gate_triggered'
    | 'decision'
    | 'documents_requested'
    | 'document_uploaded'
    | 'reminder_sent'
    | 'gate_released_approved'
    | 'gate_released_returned'
  occurred_at: string | null
  actor_name: string | null
  title: string
  detail: string | null
  kind?: string | null
  next_possible_at?: string | null
}

/** Fallakte v2 (P2/E3): Erinnerung an die Lehrperson. */
export interface DirectorRemindPayload {
  kind: 'documents' | 'info'
}

export interface DirectorRemindResponse {
  success: boolean
  message: string
  next_possible_at: string | null
}

export interface DirectorWorkflowItem extends DirectorPendingItem {
  column: WorkflowColumn
}

export interface DirectorDashboardSummary {
  pending_count: number
  approved_today_count: number
  processed_this_week_count: number
}

export const WORKFLOW_COLUMN_LABELS: Record<WorkflowColumn, string> = {
  documents_pending: 'Dokumente offen',
  ready: 'Bereit zur Entscheidung',
}

export const WORKFLOW_COLUMN_COLORS: Record<WorkflowColumn, string> = {
  documents_pending: 'border-orange-500',
  ready: 'border-green-500',
}
